import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PersonalTouchSection = () => {
  const interests = [
    {
      icon: "Telescope",
      title: "Astronomy & Space Science",
      description: "Fascinated by the cosmos — I spend hours decoding celestial events and planetary patterns."
    },
    {
      icon: "Map",
      title: "Remote Sensing & Field Work",
      description: "Passionate about collecting and analyzing data through UAVs, LiDAR, and satellite technologies."
    },
    {
      icon: "Microscope",
      title: "Scientific Research",
      description: "Committed to using data, models, and geospatial tools to solve Earth and space-based challenges."
    },
    {
      icon: "Megaphone",
      title: "STEM Outreach",
      description: "Actively mentoring students as a NASA STEM Educator and IAAC Ambassador to inspire future scientists."
    }
  ];

  const values = [
    {
      title: "Curiosity-Driven Innovation",
      description: "Fueling exploration by asking big questions and using tech to find answers."
    },
    {
      title: "Scientific Integrity",
      description: "Upholding accuracy, transparency, and reliability in every dataset and discovery."
    },
    {
      title: "Collaboration & Community",
      description: "Building knowledge through teamwork—across labs, classrooms, and global networks."
    },
    {
      title: "Impact-Oriented Learning",
      description: "Committed to research that benefits society and inspires the next generation."
    }
  ];


  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Personal Interests */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Beyond the Research
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Getting to know the explorer behind the missions. These passions shape how I innovate, communicate, and solve real-world problems.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {interests.map((interest, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-accent/20 transition-smooth">
                  <Icon 
                    name={interest.icon} 
                    size={28} 
                    className="text-accent"
                  />
                </div>
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-3">
                  {interest.title}
                </h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {interest.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Values & Philosophy */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Core Values
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
              The guiding forces behind every research initiative, outreach activity, and collaborative effort I undertake.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:shadow-accent/5 transition-smooth">
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">
                  {value.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Philosophy Quote Section */}
        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-8 lg:p-12 border border-white/10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <Icon name="Heart" size={48} className="text-error mb-6" />
              <h3 className="font-heading text-2xl font-semibold text-text-primary mb-4">
                Research Philosophy
              </h3>
              <blockquote className="text-lg text-text-secondary leading-relaxed mb-6">
                "I believe impactful research isn’t just about discovery—it’s about solving real-world problems through data, technology, and community engagement."
              </blockquote>
              <p className="text-text-secondary">
                This belief drives my work across Earth observation, atmospheric modeling, and STEM education. Whether decoding space phenomena or mentoring students, I focus on clarity, collaboration, and creating solutions that matter.
              </p>
            </div>

            <div className="text-center lg:text-right">
              <div className="inline-block relative">
                <div className="w-64 h-64 rounded-2xl overflow-hidden shadow-primary">
                  <Image
                    src="https://images.unsplash.com/photo-1446941611757-91d2c3bd3d45?w=400&h=400&fit=crop"
                    alt="Space researcher analyzing satellite data"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent/20 rounded-full blur-xl"></div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default PersonalTouchSection;